import { defineStore, storeToRefs } from "pinia";
import {
  BookingsApi,
  CompanyCatalogApi,
  SessionDetailsApi,
  SessionsApi,
  TrainingsLmsApi,
  ProjectsApi,
  type ConfirmProjectDto,
  type CreateProjectDto,
  type ProjectUserBucket,
  type UpdateProjectDto,
  type SessionItemAvailableSessionResponse,
  TrainingFormat,
} from "@simbelapp/order-sdk";
import { format } from "date-fns";
import { useTrainingPartnersStore } from "../training-partners/training-partners.store";
import { useSessionDetailsTimeslotsStore } from "../sessions/session-details-timeslots-store";
import { useSessionDetailsAttendeesStore } from "../sessions/session-details-attendees-store";
import { useFeedback } from "~/composables/feedback/useFeedback";
import { useDateChoiceStore } from "~/store/date-choice/date-choice.store";
import { useRequestsStore } from "~/store/requests/requests.store";
import { useSessionsStore } from "~/store/sessions/sessions-store";
import { useTrainingsStore } from "~/store/trainings/trainings.store";
import { ItemType, MicroserviceEnum } from "~/utils/enums/common.enums";
import { ProjectNavEnum, ProjectTypeEnum } from "~/utils/enums/project.enums";
import { SessionItemModalityEnum, SessionItemTypeEnum } from "~/utils/enums/session-items.enums";
import { useFormats } from "~/composables/utils/useFormat";
import type { ICreateProject } from "~/utils/interfaces/project-interfaces";
import { usePagination } from "~/composables/paginations/usePagination";
import { useSessionDetailsStore } from "~/store/sessions/session-details-store";
import type { IFooterActions } from "~/utils/interfaces/common-interfaces";
import { BookingStatusEnum } from "~/utils/enums/bookings-events.enums";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { TrainingTypeEnum } from "~/utils/enums/training.enums";
import { routeBaseName } from "~/utils/helpers/routeBaseName";
import type { IUsersToInvite } from "~/utils/interfaces/bookings-interfaces";
import {
  CONFIRM_PROJECT_SESSION_FULL_MESSAGE,
  exceedsCapacity,
  followsDoceboRules,
  isSessionFullError,
  SESSION_FULL_MESSAGE,
} from "~/utils/helpers/sessions/session-capacity";
import { isMultiDayTimeslotError, MULTI_DAY_TIMESLOT_MESSAGE } from "~/utils/helpers/sessions/session-timeslots";
import { useAuthStore } from "~/store/auth/auth.store";

export const ADD_TO_PLAN_ROWS_LIMIT = 1000;

export const useCreateProjectStore = defineStore("createProjectStore", {
  state: (): ICreateProject => {
    return {
      project: {
        projectId: null,
        training: {
          trainingId: null,
          projectTitle: null,
          projectItem: null,
          isNotInCatalog: false,
          lmsRules: null,
        },
        sessionItem: {
          sessionItemId: null,
          sessionItemType: null,
        },
        session: {
          sessionId: null,
          sessionList: [],
          error: {},
          createSessionStep: false,
        },
        users: {
          usersId: null,
          inscribed: null,
          selected: null,
          excludedUsersIdList: [],
          nbInscribed: null,
        },
        financing: {
          priceExclTax: null,
          budgetExclTax: null,
          opcoExclTax: null,
          cpfExclTax: null,
          capacity: null,
          usersBuckets: [],
        },
        reportingPeriod: null,
        status: null,
        comment: null,
        notification: {
          notificationType: BookingStatusEnum.BOOKING_CONFIRMED,
          doNotifyMainContact: false,
          mainContactEmail: null,
        },
        sourceType: null,
        sourceId: null,
        projectType: null,
        errorsEdit: null,
        startDate: null,
        endDate: null,
        startTime: null,
        endTime: null,
        // The planner's zone, not Europe/Paris: the step a regularization creates starts where its author is.
        timezone: useAuthStore().userTimezone,
        format: null,
        duration: null,
        location: null,
        designation: null,
        nbMaxAttendees: null,
        nbMinAttendees: null,
        overbookingNotAllowed: false,
      },
      nav: ProjectNavEnum.TRAINING,
      showExit: false,
      showModal: false,
      previousModalState: {
        modalFunnel: false,
        modalShowMultiple: false,
      },
      showConfirm: false,
      showConfirmSendNotification: false,
      showEndModal: false,
      showSessionDetailsComplete: false,
      fetchCompleteDone: false /** loading state for better UX **/,
      redirectSessionId: null,
      previousProjectId: null,
      previousSessionId: null,
      showSessionAlreadyBilledCompleted: false,
      showAddToPlanLimitModal: false,
    };
  },
  getters: {
    isTrainingChosen(): boolean {
      // Formation du catalogue OU formation hors catalogue (projectItem/projectTitle)
      return !!(
        this.project.training.trainingId ||
        this.project.training.projectItem ||
        this.project.training.projectTitle
      );
    },
    isSessionItemChosen(): boolean {
      return !!(this.isTrainingChosen && this.project.sessionItem.sessionItemId);
    },
    isTrainingStatusDraft(): boolean {
      return !!(this.project.training.trainingId?.status === "draft");
    },
    getSessionItemType(): SessionItemTypeEnum {
      return this.project.sessionItem.sessionItemType as SessionItemTypeEnum;
    },
    isSessionChosen(): boolean {
      return !!(this.isTrainingChosen && this.isSessionItemChosen && this.project.session.sessionId);
    },
    isTrainingOnDemand(): boolean {
      return (
        this.isTrainingChosen && this.project.sessionItem.sessionItemId?.modality === SessionItemModalityEnum.ON_DEMAND
      );
    },
    /** The same Docebo rule on a draft: the capacity being edited lives here, its attendees on the session details. */
    attendeesOverCapacity(): boolean {
      const details = useSessionDetailsStore().sessionDetails;
      return (
        followsDoceboRules(details?.lmsProvider) &&
        exceedsCapacity(details?.nbAttendees, 0, this.project.nbMaxAttendees)
      );
    },

    getProjectCreateApiPayload(): CreateProjectDto {
      const sessionItemType = this.project.sessionItem.sessionItemType;

      const editInfos = {
        startDate: this.project?.startDate ?? undefined,
        endDate: this.project?.endDate ?? undefined,
        format: this.project?.format?.name ?? undefined,
        designation: this.project?.designation ?? undefined,
        location: this.project?.location ?? undefined,
        duration: this.project?.duration ?? undefined,
        maxNbAttendees: this.project?.nbMaxAttendees ?? undefined,
        minNbAttendees: this.project?.nbMinAttendees ?? undefined,
        overbookingNotAllowed: this.project?.overbookingNotAllowed ?? undefined,
      };

      const existPrevious = this.previousProjectId || this.previousSessionId;

      const payload: CreateProjectDto = {
        projectId: this.project.projectId ?? undefined,
        ...editInfos,
        trainingId: this.project.training.trainingId?.trainingId ?? undefined,
        sessionItemType:
          !existPrevious && this.project.sessionItem.sessionItemType
            ? this.project.sessionItem.sessionItemType
            : undefined,
        sessionItemId:
          !existPrevious && this.project.sessionItem.sessionItemId?.sessionItemId
            ? this.project.sessionItem.sessionItemId?.sessionItemId
            : undefined,
        ...((sessionItemType === SessionItemTypeEnum.GROUP ||
          (sessionItemType === SessionItemTypeEnum.INDIVIDUAL && !this.project.users.usersId?.length)) && {
          budgetExclTax: this.project.financing.budgetExclTax ?? undefined,
        }),
        ...(!sessionItemType && {
          budgetExclTax: this.project.financing.budgetExclTax ?? undefined,
        }),
        reportingPeriodId:
          !existPrevious && this.project.reportingPeriod?.reportingId
            ? this.project.reportingPeriod?.reportingId
            : undefined,
      };

      if (this.project.users.usersId?.length) {
        if (this.project.financing.usersBuckets?.length) {
          payload.projectUsers = this.project.financing.usersBuckets?.map((u: any) => {
            return {
              userId: u.userId,
              requestId: u?.requestId,
              ...((this.project.sessionItem.sessionItemType === SessionItemTypeEnum.INDIVIDUAL ||
                sessionItemType === SessionItemTypeEnum.INDIVIDUAL) && {
                budget: Number.isFinite(u.budget) ? u.budget : 0,
              }),
              previousProjectId: this.previousProjectId,
              previousSessionId: this.previousSessionId,
            };
          });
        } else {
          payload.projectUsers = this.project.users.usersId?.map((u: any) => {
            return {
              userId: u.userId,
              requestId: u?.requestId,
              previousProjectId: this.previousProjectId,
            };
          });
        }
      }

      return payload;
    },
  },
  actions: {
    /** Maps the funnel attendees to the API buckets. The project PATCH no longer accepts them. */
    getProjectUsersPayload(): ProjectUserBucket[] | undefined {
      if (!this.project.users.usersId?.length) return undefined;

      if (this.project.financing.usersBuckets?.length) {
        return this.project.financing.usersBuckets.map((u: any) => {
          return {
            userId: u.userId,
            requestId: u?.requestId,
            previousProjectId: this.previousProjectId,
            previousSessionId: this.previousSessionId,
          };
        });
      }

      return this.project.users.usersId.map((u: any) => {
        return {
          userId: u.userId,
          requestId: u?.requestId,
          previousProjectId: this.previousProjectId,
        };
      });
    },
    getProjectUpdateApiPayload(confirmProject = false): UpdateProjectDto {
      const sessionDetailsStore = useSessionDetailsStore();
      const { sessionDetails } = storeToRefs(sessionDetailsStore);

      const editInfos = {
        startDate: this.project?.startDate ?? undefined,
        endDate: this.project.endDate ?? undefined,
        format: this.project.format?.name ?? undefined,
        designation: confirmProject
          ? (sessionDetails?.value?.designation ?? undefined)
          : (this.project?.designation ?? undefined),
        location: this.project?.location ?? undefined,
        duration: this.project?.duration ?? undefined,
        maxNbAttendees: confirmProject
          ? (sessionDetails?.value?.nbMaxAttendees ?? undefined)
          : (this.project?.nbMaxAttendees ?? undefined),
        minNbAttendees: confirmProject
          ? (sessionDetails?.value?.nbMinAttendees ?? undefined)
          : (this.project?.nbMinAttendees ?? undefined),
        overbookingNotAllowed: confirmProject
          ? (sessionDetails?.value?.overbookingNotAllowed ?? undefined)
          : (this.project?.overbookingNotAllowed ?? undefined),
      };

      return {
        projectId: this.project.projectId ?? undefined,
        ...editInfos,
        trainingId: this.project.training.trainingId?.trainingId ?? undefined,
        sessionItemType: this.project.sessionItem.sessionItemType ?? undefined,
        sessionItemId: this.project.sessionItem.sessionItemId?.sessionItemId ?? undefined,
        reportingPeriodId: this.project.reportingPeriod?.reportingId ?? undefined,
        legalEntityId: sessionDetails?.value?.legalEntityId ?? undefined,
      };
    },
    resetProjectData(): void {
      this.project = {
        ...this.project,
        session: {
          sessionList: [],
          sessionId: null,
          error: {},
          createSessionStep: false,
        },
        training: {
          trainingId: null,
          projectTitle: null,
          projectItem: null,
          isNotInCatalog: false,
          lmsRules: null,
        },
        sessionItem: {
          sessionItemId: null,
          sessionItemType: null,
        },
        financing: {
          priceExclTax: null,
          budgetExclTax: null,
          cpfExclTax: null,
          opcoExclTax: null,
          capacity: null,
          usersBuckets: [],
        },
        startDate: null,
        endDate: null,
        startTime: null,
        endTime: null,
        timezone: useAuthStore().userTimezone,
        format: null,
        duration: null,
        location: null,
        designation: null,
        nbMaxAttendees: null,
        nbMinAttendees: null,
        overbookingNotAllowed: false,
        reportingPeriod: null,
        comment: null,
        notification: {
          notificationType: BookingStatusEnum.BOOKING_CONFIRMED,
          doNotifyMainContact: false,
          mainContactEmail: null,
        },
      };
    },
    footersActions(): IFooterActions[] {
      return [
        {
          name: "Changer de session",
          logoName: "create",
          action: () => {
            // The funnel itself asks whether to register directly or delegate the date choice, at
            // its Session step (PRD US4.1) — exactly as when creating a session.
            this.changeSession();
          },
        },
      ];
    },
    openProjectModal(delegateShow = false): void {
      const sessionStore = useSessionsStore();
      sessionStore.sessionItemSessions = null;

      this.$reset();
      // The Session step must start over on the "Que voulez-vous faire ?" choice at every opening.
      useDateChoiceStore().resetFunnel();
      if (!delegateShow) {
        this.showModal = true;
      }
    },
    /** Answers false, and opens the limit modal, past ADD_TO_PLAN_ROWS_LIMIT selected rows. */
    canAddToPlan(selectedCount: number): boolean {
      if (selectedCount <= ADD_TO_PLAN_ROWS_LIMIT) return true;
      this.showAddToPlanLimitModal = true;
      return false;
    },
    closeAddToPlanLimitModal(): void {
      this.showAddToPlanLimitModal = false;
    },
    async fetchTrainingLmsRules(trainingId: string, isExternalLms?: boolean) {
      if (!isExternalLms) {
        this.project.training.lmsRules = null;
        return;
      }
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TrainingsLmsApi);
        this.project.training.lmsRules = await apiInstance.getTrainingLmsRules({ trainingId });
      } catch {
        this.project.training.lmsRules = null;
      }
    },
    async changeSession(sessionConfirmed = false) {
      useFeedback();
      const sessionDetailsStore = useSessionDetailsStore();
      const sessionDetailsTimeslotsStore = useSessionDetailsTimeslotsStore();
      const sessionStore = useSessionsStore();
      // Use the selected users that were already properly populated by updateSelectedAttendees()
      // This avoids re-fetching and ensures we have the correct data structure
      const selectedUsers = this.project.users.selected || this.project.users.usersId || [];

      this.openProjectModal(true);

      if (sessionDetailsStore.sessionDetails?.id) {
        await this.processCompleteSessionDetail(sessionDetailsStore.sessionDetails.id, true, sessionConfirmed);
      }

      // Always preserve session ID so we can return to the same session when going back
      if (sessionDetailsStore.sessionDetails?.sessionId) {
        this.previousSessionId = sessionDetailsStore.sessionDetails?.sessionId;
        if (sessionConfirmed) {
          this.previousProjectId = "";
        }
      }

      this.project.users.usersId =
        selectedUsers?.map((u: any) => {
          return {
            userId: u.userId,
            id: this.project.projectId,
            firstname: u.firstname ?? null,
            lastname: u.lastname ?? null,
            email: u.email ?? null,
            displayValue:
              u.displayValue ?? (u.firstname && u.lastname ? `${u.firstname} ${u.lastname}` : (u.email ?? "")),
          };
        }) ?? [];

      // Also set project.users.selected to ensure selectedUsersBuckets() filters correctly
      this.project.users.selected = selectedUsers ?? [];

      // Filter usersBuckets to only include the selected users
      if (this.project.financing.usersBuckets?.length && selectedUsers?.length) {
        const selectedUserIds = selectedUsers.map((u: any) => u.userId);
        this.project.financing.usersBuckets = this.project.financing.usersBuckets.filter((bucket: any) =>
          selectedUserIds.includes(bucket.userId),
        );
      }

      this.project.projectType = ProjectTypeEnum.NEW_PROJECT;
      this.previousModalState.modalShowMultiple = true;
      this.previousProjectId = this.project.projectId;
      this.project.session.sessionList = [];

      if (this.project.training.trainingId?.trainingId) {
        this.project.session.sessionList = await sessionStore.fetchSessionsByTrainingId({
          trainingId: this.project.training.trainingId?.trainingId,
          inProgress: "true",
        });
        this.project.session.sessionList?.forEach((session: any) => {
          session.isCheck = false;
        });
      }

      const { openAccordionAttendees } = storeToRefs(sessionDetailsTimeslotsStore);
      if (openAccordionAttendees.value) {
        await sessionDetailsTimeslotsStore.fetchSessionTimeslotsAttendees(openAccordionAttendees.value);
      }
      this.showModal = true;
    },
    async performImpactsTrainingChange() {
      const sessionStore = useSessionsStore();
      this.project.session.sessionList = [];
      if (this.project.training.trainingId?.trainingId) {
        this.project.training.projectTitle = null;
        this.project.training.projectItem = null;
        this.project.duration = this.project.training.trainingId?.durationHours ?? null;
        await this.fetchTrainingLmsRules(
          this.project.training.trainingId.trainingId,
          this.project.training.trainingId.isExternalLms,
        );
      }

      sessionStore.sessionItemSessions = null;

      this.project.sessionItem.sessionItemType = null;
      this.project.sessionItem.sessionItemId = null;
      this.project.session.sessionId = null;
      this.project.financing.priceExclTax = null;
      this.project.financing.budgetExclTax = null;
      this.project.financing.cpfExclTax = null;
      this.project.financing.opcoExclTax = null;

      const types = this.project.training.trainingId?.sessionItemsTypes;
      const isIndividualSession = types?.some((type) => type === SessionItemTypeEnum.INDIVIDUAL);
      const isGroupSession = types?.some((type) => type === SessionItemTypeEnum.GROUP);

      if (!isGroupSession && isIndividualSession && this.project.training.trainingId?.individualSessionItem) {
        this.project.sessionItem.sessionItemId = this.project.training.trainingId?.individualSessionItem ?? null;
        this.project.sessionItem.sessionItemType = SessionItemTypeEnum.INDIVIDUAL;
        this.project.nbMaxAttendees = this.project.training.trainingId?.individualSessionItem?.maxNbAttendees ?? null;
        this.project.nbMinAttendees = this.project.training.trainingId?.individualSessionItem?.minNbAttendees ?? null;
        this.project.overbookingNotAllowed =
          this.project.training.trainingId?.individualSessionItem?.overbookingNotAllowed ?? false;
      }

      // Symmetric to the individual-only case above (mirrors the "type d'inscription" switch):
      // when the training exposes a single session-item type, auto-select it so the draft is
      // created with its session item + capacity. When both types exist, leave it unset for
      // the user to choose.
      if (!isIndividualSession && isGroupSession && this.project.training.trainingId?.groupSessionItem) {
        this.project.sessionItem.sessionItemId = this.project.training.trainingId?.groupSessionItem ?? null;
        this.project.sessionItem.sessionItemType = SessionItemTypeEnum.GROUP;
        this.project.nbMaxAttendees = this.project.training.trainingId?.groupSessionItem?.maxNbAttendees ?? null;
        this.project.nbMinAttendees = this.project.training.trainingId?.groupSessionItem?.minNbAttendees ?? null;
        this.project.overbookingNotAllowed =
          this.project.training.trainingId?.groupSessionItem?.overbookingNotAllowed ?? false;
      }

      if (this.project.sessionItem.sessionItemType) {
        this.setSessionItemsSessions(this.project.sessionItem?.sessionItemType);
      }

      if (this.project.training.trainingId?.trainingPartnerId) {
        const trainingPartnersStore = useTrainingPartnersStore();
        const tp = await trainingPartnersStore.findOneTrainingPartner(
          this.project.training.trainingId?.trainingPartnerId,
        );

        this.project.notification.mainContactEmail = tp?.mainContactEmail;
      }

      if (this.project.training.trainingId?.trainingPartner?.mainContactEmail) {
        this.project.notification.mainContactEmail =
          this.project.training.trainingId?.trainingPartner?.mainContactEmail;
      }

      this.project.session.sessionList = [];
      if (this.project.training.trainingId?.trainingId) {
        this.project.session.sessionList = await sessionStore.fetchSessionsByTrainingId({
          trainingId: this.project.training.trainingId?.trainingId,
          inProgress: "true",
        });
        this.project.session.sessionList?.forEach((session: any) => {
          session.isCheck = false;
        });
      }
    },
    async performImpactsSessionChange(session: SessionItemAvailableSessionResponse) {
      this.project.users.inscribed = null;

      if (session.sessionId) {
        this.project.session.sessionId = session;
        if (this.project.sourceType !== ItemType.REQUEST) {
          const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, SessionDetailsApi);
          const data = await apiInstance.getBasicAttendees({
            itemId: session.sessionId,
            itemType: ItemType.SESSION,
          });
          const attendees = data.data?.filter((a: any) => a.sessionId !== "fake");
          this.project.users.inscribed = attendees ?? null;
          this.project.users.usersId = attendees ?? null;
          this.project.users.nbInscribed = Number(data.meta?.count) || 0;
        }
      }
    },
    async setSessionItemsSessions(val: SessionItemTypeEnum, dontLoadValues: boolean = false) {
      const sessionsStore = useSessionsStore();
      if (this.project.training.trainingId && val) {
        const sessionItems: Record<string, any> = {
          group: this.project.training.trainingId?.groupSessionItem,
          individual: this.project.training.trainingId?.individualSessionItem,
        };

        this.project.sessionItem.sessionItemId = sessionItems[val];
        this.project.financing.priceExclTax = sessionItems[val]?.priceExclTax;
        this.project.financing.budgetExclTax = sessionItems[val]?.priceExclTax;

        if (this.project.sessionItem?.sessionItemId) {
          if (this.project.sessionItem.sessionItemId.modality !== SessionItemModalityEnum.ON_DEMAND) {
            await sessionsStore.fetchSessionItemSessions(this.project.sessionItem?.sessionItemId.sessionItemId);
          } else if (this.project.sessionItem.sessionItemId.modality === SessionItemModalityEnum.ON_DEMAND) {
            this.setOnDemandDates();
          }

          if (this.project.sessionItem.sessionItemType === SessionItemTypeEnum.INDIVIDUAL && !dontLoadValues) {
            this.project.financing.usersBuckets?.forEach((user: any) => {
              user.budget = this.project.financing.budgetExclTax;
              user.opco = this.project.financing.opcoExclTax;
              user.cpf = this.project.financing.cpfExclTax;
            });
          }
        }
      }
    },
    loadUsersFinancing() {
      const requestsStore = useRequestsStore();

      let sourceBudget: number | null = null;

      if (this.project.sourceType === ItemType.REQUEST && Number.isFinite(requestsStore.requestDetails?.priceExclTax)) {
        sourceBudget = Number(requestsStore.requestDetails?.priceExclTax);
      } else if (this.isSessionItemChosen && Number.isFinite(this.project.sessionItem.sessionItemId?.priceExclTax)) {
        sourceBudget = Number(this.project.sessionItem.sessionItemId?.priceExclTax);
      }
      if (this.project.users.usersId?.length) {
        const training = this.project.training.trainingId;
        const isIndividualSession =
          training?.individualSessionItem && this.getSessionItemType === SessionItemTypeEnum.INDIVIDUAL;
        if (this.project.projectType === ProjectTypeEnum.REGULATION && isIndividualSession && sourceBudget) {
          sourceBudget = sourceBudget * (this.project.users.usersId.length || 1);
          this.project.financing.priceExclTax = sourceBudget;
          this.project.financing.budgetExclTax = sourceBudget;
        }
        this.project.financing.usersBuckets = null;

        this.project.financing.usersBuckets = this.project.users.usersId?.map((u) => {
          return {
            userId: u.userId,
            requestId: u?.requestId,
            display: u.firstname && u.lastname ? `${u.firstname} ${u.lastname}` : (u.email ?? ""),
            budget: Number(sourceBudget),
            cpf: 0,
            opco: 0,
            total: Number.isFinite(sourceBudget) ? Number(sourceBudget) : 0,
          };
        });
      } else if (!this.project.users.usersId?.length) {
        this.project.financing.usersBuckets = [];
      }
    },
    setOnDemandDates() {
      const training = this.project.training.trainingId;
      if (this.isTrainingOnDemand) {
        this.project.session.createSessionStep = true;

        const endDate = training?.durationDays
          ? new Date(new Date().setDate(new Date().getDate() + training.durationDays))
          : new Date();

        this.project.startDate = format(new Date(), "yyyy-MM-dd");
        this.project.endDate = format(new Date(endDate), "yyyy-MM-dd");
      }
    },
    async updatedTrainingAndSessionItem() {
      const trainingsStore = useTrainingsStore();

      if (
        this.project.training?.trainingId &&
        !Number.isFinite(this.project.training.trainingId?.durationHours) &&
        Number.isFinite(this.project.training.trainingId.newTrainingDurationHours)
      ) {
        await trainingsStore.createUpdateTraining({
          trainingId: this.project.training.trainingId.trainingId,
          durationHours: this.project.training.trainingId?.newTrainingDurationHours,
        });

        this.project.training.trainingId.durationHours = this.project.training.trainingId
          .newTrainingDurationHours as number;
      }

      /** The format belongs to the training: a single write, no more propagation to session items. */
      if (this.project.training?.trainingId?.newTrainingFormat) {
        await trainingsStore.createUpdateTraining({
          trainingId: this.project.training.trainingId.trainingId,
          format: this.project.training.trainingId.newTrainingFormat,
        });

        this.project.training.trainingId.format = this.project.training.trainingId.newTrainingFormat;
      }
    },
    async createProject(removeProjectID: boolean = true) {
      const feedback = useFeedback();

      if (!this.project.users.usersId?.length) {
        this.loadUsersFinancing();
      }

      if (this.isTrainingStatusDraft) {
        await this.updatedTrainingAndSessionItem();
      }

      let createProjectDto = this.getProjectCreateApiPayload;
      if (removeProjectID) {
        createProjectDto = { ...createProjectDto, projectId: undefined };
      }

      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, ProjectsApi);
        const data = await apiInstance.createProject({
          createProjectDto: { title: this.project.training.projectTitle ?? undefined, ...createProjectDto },
        });

        this.$reset();
        feedback.success(`Brouillon sauvegardé`, "small");
        this.redirectProject(data.projectId, ItemType.PROJECT);
      } catch {
        this.$reset();
        feedback.error(`erreur lors de la sauvegarde du brouillon`, "small");
      }

      this.previousModalState.modalShowMultiple = false;
    },
    /** @returns whether the draft was actually persisted — callers must not close on `false`. */
    async editProject(): Promise<boolean> {
      const feedback = useFeedback();
      const sessionDetailsStore = useSessionDetailsStore();

      try {
        if (!this.project.users.usersId?.length) {
          this.loadUsersFinancing();
        }

        // Inside the `try` on purpose: this writes the training and its session item BEFORE the
        // project itself, and it can reject. Left outside, `editProject()` rejected instead of
        // returning `false`, so `ProjectEditModal.saveProject()` — which awaits it with no catch —
        // got an uncaught rejection rather than the documented boolean, and neither the error toast
        // nor the "keep the modal open" branch ran.
        if (this.isTrainingStatusDraft) {
          await this.updatedTrainingAndSessionItem();
        }
        const updateProjectDto = this.getProjectUpdateApiPayload();

        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, ProjectsApi);
        const data = await apiInstance.updateProject({
          projectId: this.project.projectId!,
          updateProjectDto: { title: this.project.training.projectTitle ?? undefined, ...updateProjectDto },
        });

        this.$reset();
        feedback.success(`Brouillon sauvegardé`, "small");

        if (!sessionDetailsStore.sessionViewModal) {
          const usePaginate = usePagination();
          await usePaginate.fetchData();
        }

        this.redirectProject(data.projectId, ItemType.PROJECT);
        return true;
      } catch {
        // No `$reset()` here: the modal now stays open on failure, so wiping the store would hand
        // the user an empty form instead of the values they just tried to save.
        feedback.error(`erreur lors de la sauvegarde du brouillon`, "small");
        return false;
      }
    },
    async confirmProject() {
      const feedback = useFeedback();
      const sessionDetailsStore = useSessionDetailsStore();

      // If a session was selected in "Changer de session" flow, switch to it now
      if (this.project.session.sessionId && this.previousSessionId) {
        await sessionDetailsStore.fetchSessionDetails(this.project.session.sessionId.sessionId ?? "", ItemType.SESSION);
      }

      const trainingsStore = useTrainingsStore();

      const trainingCategories = this.project.training.trainingId?.categories?.map((c) => c.categoryId);

      await trainingsStore.createUpdateTraining({
        trainingId: this.project.training.trainingId?.trainingId,
        isNotBillable:
          sessionDetailsStore.sessionDetails?.sessionId &&
          sessionDetailsStore.sessionDetails.trainingType !== TrainingTypeEnum.INTERNAL
            ? sessionDetailsStore.sessionDetails?.isNotBillable
            : this.project.training.trainingId?.isNotBillable,
        ...(trainingCategories?.length && {
          categories: trainingCategories.filter((category): category is string => !!category),
        }),
      });
      const confirmProjectDto: ConfirmProjectDto = {
        ...this.getProjectUpdateApiPayload(true),
        projectUsers: this.getProjectUsersPayload(),
      };

      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, ProjectsApi);
        const data = await apiInstance.confirmProject({
          confirmProjectDto: {
            ...confirmProjectDto,
            comment: this.project.comment ?? undefined,
            bookingsStatus: this.project.notification.notificationType ?? undefined,
            trainingPartnerEmail:
              this.project.notification.doNotifyMainContact && this.project.notification.mainContactEmail
                ? this.project.notification.mainContactEmail
                : undefined,
          },
        });

        if (data.sessionId) {
          feedback.greeting(`Nouvelle session créée`, "small");
          this.redirectSessionId = data.sessionId;
          if (BookingStatusEnum.BOOKING_CONFIRMED === this.project.notification.notificationType) {
            this.showConfirm = false;
            this.showConfirmSendNotification = true;
          } else {
            this.redirectProject(data.sessionId, ItemType.SESSION);
          }
        } else {
          feedback.error(`erreur lors de la confirmation du project`, "small");
        }
      } catch (error: any) {
        if (error?.message === "Users already invited to this session") {
          feedback.error(`Un des participants est déjà inscrit à cette session`, "small");
          return;
        }
        if (isSessionFullError(error)) {
          feedback.error(CONFIRM_PROJECT_SESSION_FULL_MESSAGE, "small");
          return;
        }
        if (isMultiDayTimeslotError(error)) {
          feedback.error(MULTI_DAY_TIMESLOT_MESSAGE, "small");
          return;
        }
        feedback.error(`erreur lors de la confirmation du project`, "small");
      }
    },

    /**
     * Confirm regulation flow - creates a new session from project data
     * This is the legacy confirmProject from create-project.store.ts adapted for flat structure
     */
    async confirmRegulation() {
      const feedback = useFeedback();
      const sessionStore = useSessionsStore();
      const formats = useFormats();

      const { session, sessionItem, training } = this.project;

      let newSession = null;
      if (sessionItem.sessionItemId?.sessionItemId) {
        if (session?.sessionId) {
          this.showModal = false;
          this.redirectProject(session?.sessionId.sessionId ?? "", ItemType.SESSION);
          this.$reset();
        } else if (this.project.startDate && this.project.endDate) {
          // Use flat structure fields instead of session_draft
          newSession = await sessionStore.createSession({
            sessionItemId: sessionItem.sessionItemId?.sessionItemId,
            startDate: formats.toDateISOString(new Date()),
            endDate: formats.toDateISOString(new Date()),
            trainingAddress: this.project.location,
            designation: this.project.designation,
            maxNbAttendees: this.project.nbMaxAttendees,
            minNbAttendees: sessionItem.sessionItemId.minNbAttendees,
            overbookingNotAllowed: sessionItem.sessionItemId.overbookingNotAllowed,
            sessionTimeslot: [
              {
                startDate: formats.toDateISOString(new Date(this.project.startDate)),
                endDate: formats.toDateISOString(new Date(this.project.endDate)),
                startTime: this.project.startTime ?? undefined,
                endTime: this.project.endTime ?? undefined,
                timezone: this.project.timezone,
                format: (this.project.format?.name ?? training.trainingId?.format) as TrainingFormat,
                trainingDurationHours: training.trainingId?.durationHours ?? 0,
                location: this.project.location ?? undefined,
                name: this.project.designation,
              },
            ],
          });

          if (newSession && newSession.sessionId) {
            feedback.greeting(`On est tout bon !`, "small");
            this.redirectSessionId = newSession.sessionId;
            this.showModal = false;
            this.redirectProject(newSession.sessionId, ItemType.SESSION);
          } else {
            feedback.error(`Erreur lors de la régularisation de session`, "small");
          }
        }
      }
    },

    async redirectProject(id: string, type: ItemType) {
      const $router = useRouter();
      // i18n suffixes route names with the locale ("sessions___fr"), so compare the base name.
      const routeName: string = routeBaseName($router.currentRoute.value.name);
      const sessionDetailsStore = useSessionDetailsStore();
      const sessionDetailsAttendeesStore = useSessionDetailsAttendeesStore();

      this.$reset();
      // We don't want to redirect to the session details page when it's a training admin
      if (routeName.includes("my-sessions")) {
        await $router.push({
          query: {
            id,
            type,
          },
        });

        await sessionDetailsStore.fetchSessionDetails(id, type);
        await sessionDetailsAttendeesStore.fetchSessionAttendeesFullDatas(id, type);
        sessionDetailsStore.sessionViewModal = true;

        return;
      }

      // Only /sessions mounts the session-detail modal, so it's the only route where showMySession's
      // query-only push lands somewhere visible; from anywhere else we have to navigate there.
      if (routeName === "sessions") {
        await sessionDetailsStore.showMySession(id, type);
      } else {
        return navigateTo({
          path: `/sessions`,
          query: {
            id,
            type,
          },
        });
      }
    },
    closeProjectModals() {
      this.showModal = false;
      this.showSessionDetailsComplete = false;
    },
    /**
     * Opens the "send a notification?" prompt once attendees have been registered.
     * Its only caller registers them outside the funnel, so the funnel draft is dropped first: a
     * leftover session list mislabels the prompt and a leftover redirectSessionId lands the admin
     * on another session.
     */
    openConfirmSendNotification(): void {
      this.$reset();
      this.showConfirmSendNotification = true;
    },
    closeConfirmSendNotification(): void {
      this.showConfirmSendNotification = false;
    },
    handleProjectModalStateChanges() {
      const prevModalState = this.previousModalState;

      if (prevModalState.modalFunnel) {
        this.showModal = true;
      }
    },
    /**
     * @description - This function is used to invite users from the selected users buckets
     * @var this.previousModalState.modalShowMultiple - Check if the modal is in multiple mode
     * @var this.project.financing.usersBuckets - Get the users buckets
     * @returns {IUsersToInvite[]} - Return the users to invite with the correct budget
     */
    selectedUsersBuckets(): IUsersToInvite[] {
      const sessionStore = useSessionsStore();
      const opco = (this.project.financing.opcoExclTax || 0) / (this.project.financing.usersBuckets?.length || 1);
      const cpf = (this.project.financing.cpfExclTax || 0) / (this.project.financing.usersBuckets?.length || 1);
      const budget = (this.project.financing.budgetExclTax || 0) / (this.project.financing.usersBuckets?.length || 1);
      let filteredMultiUsersBuckets: IUsersToInvite[] = [];

      if (this.project.financing.usersBuckets?.length) {
        if (this.previousModalState.modalShowMultiple) {
          let multiUsersBuckets: IUsersToInvite[] = [];
          if (
            sessionStore.sessions?.length &&
            sessionStore.sessions?.find((s) => s.isCheck && s.sessionItemType === SessionItemTypeEnum.INDIVIDUAL)
          ) {
            multiUsersBuckets = this.project.financing.usersBuckets.map((user: any) => {
              return {
                userId: user.userId,
                previousProjectId: this.previousProjectId,
                previousSessionId: this.previousSessionId,
                opco: user.opco || undefined,
                cpf: user.cpf || undefined,
                budget: user.budget || undefined,
              };
            });
          } else {
            multiUsersBuckets = this.project.financing.usersBuckets?.map((user: any) => {
              return {
                userId: user.userId,
                previousProjectId: this.previousProjectId,
                previousSessionId: this.previousSessionId,
                opco: opco || undefined,
                cpf: cpf || undefined,
                budget: budget || undefined,
              };
            });
          }
          const selectedIds = this.project.users.selected?.map((u) => u.userId) || [];
          filteredMultiUsersBuckets = multiUsersBuckets.filter((user) => selectedIds.includes(user.userId));
        } else {
          filteredMultiUsersBuckets = this.project.financing.usersBuckets.map((user: any) => {
            return {
              userId: user.userId,
              previousProjectId: this.previousProjectId,
              previousSessionId: this.previousSessionId,
              opco: opco || undefined,
              cpf: cpf || undefined,
              budget: budget || undefined,
              ...(user?.requestId && { sourceType: ItemType.REQUEST, sourceId: user.requestId }),
            };
          });
        }
      }

      return filteredMultiUsersBuckets;
    },

    async createBookings() {
      if (!this.project.financing.usersBuckets?.length) {
        return;
      }

      if (this.isTrainingStatusDraft) {
        await this.updatedTrainingAndSessionItem();
      }

      const feedback = useFeedback();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, BookingsApi);

      try {
        const ses = this.project.session.sessionList?.find((s) => s.isCheck);
        const filteredMultiUsersBuckets = this.selectedUsersBuckets();
        const usersCamelCase = filteredMultiUsersBuckets.map((u) => ({
          userId: u.userId,
          ...(u.previousProjectId && { previousProjectId: u.previousProjectId }),
          ...(u.previousSessionId && { previousSessionId: u.previousSessionId }),
          ...(this.project.sourceType && { sourceType: this.project.sourceType }),
          ...(this.project.sourceId && { sourceId: this.project.sourceId }),
          ...(u.sourceType && { sourceType: u.sourceType }),
          ...(u.sourceId && { sourceId: u.sourceId }),
        }));

        const createBookings = await apiInstance.createBooking({
          createBookingsDto: {
            usersToInvite: usersCamelCase,
            sessionId: ses?.sessionId,
            trainingId: this.project.training.trainingId?.trainingId,
            comment: this.project.comment ?? undefined,
            doNotNotify: this.project.notification.doNotifyMainContact,
            bookingsStatus: this.project.notification.notificationType as string,
            trainingPartnerEmail:
              this.project.notification.doNotifyMainContact && this.project.notification.mainContactEmail
                ? this.project.notification.mainContactEmail
                : undefined,
          },
        });

        if (createBookings.sessionId) {
          const sessionDetailsStore = useSessionDetailsStore();
          await sessionDetailsStore.fetchSessionAttendeesBasic(createBookings.sessionId, ItemType.SESSION);
          const { sessionDetailsAttendeesBasic } = storeToRefs(sessionDetailsStore);

          if (sessionDetailsAttendeesBasic?.value?.data?.length && this.project.users.usersId?.length) {
            this.project.users.usersId = this.project.users.usersId.map((u) => {
              return {
                ...u,
                sessionId: createBookings.sessionId,
                originalId: sessionDetailsAttendeesBasic?.value?.data?.find((s) => s.userId === u.userId)?.id,
              };
            });
          }

          feedback.greeting(`Les participants ont bien été inscrits`, "small");
          this.redirectSessionId = createBookings.sessionId;
          if (BookingStatusEnum.BOOKING_CONFIRMED === this.project.notification.notificationType) {
            this.showConfirm = false;
            this.showConfirmSendNotification = true;
          } else {
            this.redirectProject(createBookings.sessionId, ItemType.SESSION);
          }
        } else {
          feedback.error(`erreur lors de la confirmation du project`, "small");
        }
      } catch (error: any) {
        if (error?.message === "Users already invited to this session") {
          feedback.error(`Un des participants est déjà inscrit à cette session`, "small");
          return;
        }
        if (isSessionFullError(error)) {
          feedback.error(SESSION_FULL_MESSAGE, "small");
          return;
        }
        feedback.error(`erreur lors de la confirmation du project`, "small");
      }
    },

    async addUsersToProject() {
      const feedback = useFeedback();
      const project = this.project;
      const selectedProject = project.session?.sessionList?.find(
        (item: any) => item.isCheck && item.status === ItemType.PROJECT,
      );
      const users = project.users.selected || project.users.usersId;

      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, ProjectsApi);
        await apiInstance.projectsControllerAddProjectAttendees({
          addProjectAttendeesDto: {
            projectId: selectedProject?.projectId,
            projectUsers: users?.map((u) => {
              return {
                previousProjectId: this.previousProjectId,
                previousSessionId: this.previousSessionId,
                userId: u.userId,
                requestId: u?.requestId || null,
              };
            }),
          },
        });
      } catch {
        feedback.error(`erreur`, "small");
      }
    },

    async addAttendeesToProject(projectId: string, projectUsers: ProjectUserBucket[]): Promise<boolean> {
      const feedback = useFeedback();
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, ProjectsApi);
        await apiInstance.projectsControllerAddProjectAttendees({
          addProjectAttendeesDto: { projectId, projectUsers },
        });
        return true;
      } catch {
        feedback.error(`erreur`, "small");
        return false;
      }
    },

    async startProjectFromRequest() {
      this.showModal = true;
      await this.processHRRequest();
      this.project.projectType = ProjectTypeEnum.NEW_PROJECT;
    },
    removeFinancingUserBucket(userId: string) {
      const buckets = this.project.financing.usersBuckets;
      if (!buckets) return;
      const index = buckets.findIndex((u) => u.userId === userId);
      if (index !== -1) buckets.splice(index, 1);
    },
    async processHRRequest() {
      this.$reset();

      const requestsStore = useRequestsStore();
      const { requestDetails } = storeToRefs(requestsStore);

      if (requestDetails.value?.requestId && requestDetails.value.notProcessed) {
        this.project.sourceId = requestDetails.value?.requestId;
        this.project.sourceType = ItemType.REQUEST;

        if (requestDetails.value?.trainingId) {
          const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, CompanyCatalogApi);
          const response = await apiInstance.getCompanyCatalog({ trainingId: requestDetails.value?.trainingId });

          this.project.training.trainingId = response.data.find(Boolean) ?? null;

          if (this.project.training.trainingId?.trainingId) {
            await this.fetchTrainingLmsRules(
              this.project.training.trainingId.trainingId,
              this.project.training.trainingId.isExternalLms,
            );
          }
        }

        if (this.isTrainingOnDemand) {
          this.setOnDemandDates();
        }

        if (requestDetails.value?.userId) {
          /**
           * API call return an array whereas its a findOne, but here we can use it to init the null array, may be legacy latter
           */

          this.project.users.usersId = [
            {
              requestId: requestDetails.value?.requestId,
              userId: requestDetails.value?.userId,
              id: this.project.projectId,
              firstname: requestDetails.value?.userFirstname ?? null,
              lastname: requestDetails.value?.userLastname ?? null,
              email: requestDetails.value?.userEmail ?? null,
              displayValue: requestDetails.value?.userDisplayValue ?? null,
              managerEmail: requestDetails.value?.managerEmail ?? null,
              managerId: requestDetails.value?.userManagerId ?? null,
            },
          ];

          this.loadUsersFinancing();
        }

        this.project.session.sessionList = [];
        if (this.project.training.trainingId?.trainingId) {
          const sessionStore = useSessionsStore();
          const requestsStore = useRequestsStore();

          const { requestDetails } = storeToRefs(requestsStore);
          const sessions = await sessionStore.fetchSessionsByTrainingId({
            trainingId: this.project.training.trainingId?.trainingId,
            inProgress: "true",
          });
          this.project.session.sessionList =
            sessions?.map((session: any) => ({
              ...session,
              isCheck: !!(requestDetails.value?.sessionId && requestDetails.value.sessionId === session.sessionId),
            })) ?? [];
        }
        this.showModal = true;
      }
    },
    async validateAndCompleteSessionDetail(id: string): Promise<boolean> {
      const sessionDetailsStore = useSessionDetailsStore();
      const sessionDetailsTimeslotsStore = useSessionDetailsTimeslotsStore();
      const sessionDetails = sessionDetailsStore.sessionDetails;
      const sessionTimeslots = sessionDetailsTimeslotsStore.sessionTimeslots;

      this.project.projectType = ProjectTypeEnum.NEW_PROJECT;

      const errors = {
        ...(!this.project.training?.trainingId && { trainingId: "Veuillez sélectionner une formation" }),
        ...(sessionDetails?.sessionType === null && {
          sessionItemType: "Veuillez sélectionner un type de session",
        }),
        ...(!sessionDetails?.endDate && { endDate: "Veuillez sélectionner une date de fin" }),
        ...(!sessionDetails?.startDate && { startDate: "Veuillez sélectionner une date de début" }),
        ...(sessionTimeslots && !sessionTimeslots.length && { startDate: "Veuillez valider l'étape" }),
        ...(sessionTimeslots &&
          sessionTimeslots.length > 1 &&
          sessionDetailsStore.isLms && {
            sessionTimeslotLms: "Pour une formation interne 100% en ligne, vous ne pouvez ajouter qu'une seule étape.",
          }),
      };

      if (Object.keys(errors).length === 0) {
        await this.processCompleteSessionDetail(id);
        this.showConfirm = true;
        return true;
      }

      this.project.errorsEdit = true;
      return false;
    },
    async processCompleteSessionDetail(id: string, noReset?: boolean, isSession?: boolean) {
      const feedback = useFeedback();
      const sessionsApi = await useSDKApi(MicroserviceEnum.ORDERS, SessionsApi);
      const projectsApi = await useSDKApi(MicroserviceEnum.ORDERS, ProjectsApi);

      if (!noReset) {
        this.$reset();
      }

      this.fetchCompleteDone = false;

      const applyBucketsFinancing = (
        buckets: Array<{ priceExclTax: number | null; financingMode: { name: string } | null }>,
      ) => {
        if (
          this.project.sessionItem.sessionItemType !== SessionItemTypeEnum.GROUP &&
          this.project.sessionItem.sessionItemType !== null
        ) {
          return;
        }
        const budget = buckets?.find((b) => b.financingMode?.name === "budget")?.priceExclTax;
        const opco = buckets?.find((b) => b.financingMode?.name === "opco")?.priceExclTax;
        const cpf = buckets?.find((b) => b.financingMode?.name === "cpf")?.priceExclTax;
        this.project.financing.budgetExclTax = Number.isFinite(budget) ? Number(budget) : null;
        this.project.financing.opcoExclTax = Number.isFinite(opco) ? Number(opco) : null;
        this.project.financing.cpfExclTax = Number.isFinite(cpf) ? Number(cpf) : null;
        this.project.financing.priceExclTax = Number(
          (this.project.financing.budgetExclTax || 0) +
            (this.project.financing.opcoExclTax || 0) +
            (this.project.financing.cpfExclTax || 0),
        );
      };

      const displayName = (
        user?: { firstname?: string | null; lastname?: string | null; email?: string | null } | null,
      ) => (user?.firstname && user?.lastname ? `${user.firstname} ${user.lastname}` : (user?.email ?? null));

      try {
        if (isSession) {
          const s = await sessionsApi.getSessionDetails({ sessionId: id });

          this.project.projectId = null;

          const training = s.sessionItem?.training;
          const group = training?.groupSessionItem;
          const individual = training?.individualSessionItem;
          this.project.training.trainingId = training
            ? {
                ...training,
                sessionItemsTypes: training.sessionItemsTypes?.length
                  ? training.sessionItemsTypes
                  : [...new Set([group?.type, individual?.type].filter((t): t is string => !!t))],
                groupPriceExclTax: training.groupPriceExclTax ?? group?.priceExclTax,
                individualPriceExclTax: training.individualPriceExclTax ?? individual?.priceExclTax,
              }
            : null;

          this.project.sessionItem.sessionItemType = (s.sessionItem?.type as SessionItemTypeEnum) ?? null;
          if (this.project.sessionItem.sessionItemType) {
            this.setSessionItemsSessions(this.project.sessionItem.sessionItemType, true);
          }
          if (this.project.overbookingNotAllowed === null) {
            this.project.overbookingNotAllowed = false;
          }
          this.project.session.sessionId = {
            sessionId: s.sessionId,
            designation: s.designation,
            startDate: s.startDate,
            endDate: s.endDate,
            trainingPartnerEmail: s.trainingPartnerEmail,
          };

          applyBucketsFinancing(s.buckets);

          this.project.financing.usersBuckets = s.bookings?.map((u) => {
            const budget = u.buckets?.find((b) => b.financingMode?.name === "budget")?.priceExclTax;
            const cpf = u.buckets?.find((b) => b.financingMode?.name === "cpf")?.priceExclTax;
            const opco = u.buckets?.find((b) => b.financingMode?.name === "opco")?.priceExclTax;
            return {
              userId: u.user?.userId,
              display: displayName(u.user) ?? "",
              budget: Number.isFinite(budget) ? Number(budget) : null,
              cpf: Number.isFinite(cpf) ? Number(cpf) : null,
              opco: Number.isFinite(opco) ? Number(opco) : null,
              total: null,
            };
          });
          this.project.users.usersId = (s.bookings ?? []).map((pu) => ({
            userId: pu.user?.userId,
            firstname: pu.user?.firstname,
            lastname: pu.user?.lastname,
            email: pu.user?.email,
            displayValue: displayName(pu.user),
          }));

          this.project.notification.doNotifyMainContact = !!s.trainingPartnerEmail;
          this.project.notification.mainContactEmail = s.trainingPartnerEmail ?? null;
        } else {
          const s = await projectsApi.findOneProjectDetails({ projectId: id });

          this.project.projectId = s.projectId;
          if (!s.training) {
            this.project.training.projectTitle = s.title;
          }
          this.project.training.trainingId = s.training ?? null;

          this.project.sessionItem.sessionItemId = s.sessionItem ?? null;
          this.project.sessionItem.sessionItemType = (s.sessionItemType as SessionItemTypeEnum) ?? null;
          if (this.project.sessionItem.sessionItemType) {
            this.setSessionItemsSessions(this.project.sessionItem.sessionItemType, true);
          }
          if (this.project.overbookingNotAllowed === null) {
            this.project.overbookingNotAllowed = false;
          }
          this.project.session.sessionId = s.session ? { sessionId: s.session.sessionId } : null;

          applyBucketsFinancing(s.buckets);

          this.project.financing.usersBuckets = s.projectUsers?.map((u) => {
            const budget = u.buckets?.find((b) => b.financingMode?.name === "budget")?.priceExclTax;
            const cpf = u.buckets?.find((b) => b.financingMode?.name === "cpf")?.priceExclTax;
            const opco = u.buckets?.find((b) => b.financingMode?.name === "opco")?.priceExclTax;
            return {
              userId: u.user?.userId,
              display: displayName(u.user) ?? "",
              budget: Number.isFinite(budget) ? Number(budget) : null,
              cpf: Number.isFinite(cpf) ? Number(cpf) : null,
              opco: Number.isFinite(opco) ? Number(opco) : null,
              total: null,
            };
          });
          this.project.users.usersId = (s.projectUsers ?? []).map((pu) => ({
            userId: pu.user?.userId,
            firstname: pu.user?.firstname,
            lastname: pu.user?.lastname,
            email: pu.user?.email,
            displayValue: displayName(pu.user),
            managerEmail: pu.user?.manager?.email ?? null,
            managerDisplayValue: displayName(pu.user?.manager),
          }));

          if (
            this.project.users.selected?.length &&
            this.project.users.usersId?.length &&
            this.project.financing.usersBuckets?.length
          ) {
            this.project.users.usersId = this.project.users.usersId?.filter((u: any) =>
              this.project.users.selected?.find((su: any) => su.userId === u.userId),
            );
            this.project.financing.usersBuckets = this.project.financing.usersBuckets?.filter((u: any) =>
              this.project.users.selected?.find((su: any) => su.userId === u.userId),
            );
          }

          if (s.reportingPeriod) {
            this.project.reportingPeriod = { ...s.reportingPeriod, entities: [] };
          }

          this.project.notification.doNotifyMainContact = !!s.trainingPartnerEmail;
          this.project.notification.mainContactEmail = s.trainingPartnerEmail ?? null;
        }
      } catch {
        feedback.error("Une erreur est survenue", "small");
      }

      this.fetchCompleteDone = true;
    },
  },
});
